import React, { useMemo, useState } from "react";

const getArticleCount = (asset) =>
  Number(asset?.total_news_count ?? asset?.news?.length ?? 0);

const truncateLabel = (value = "", maximumLength = 22) =>
  value.length > maximumLength
    ? `${value.slice(0, maximumLength - 1)}…`
    : value;

const NewsRadarDashboard = ({ groups = [], onSelectGroup }) => {
  const [activeGroupIndex, setActiveGroupIndex] = useState(null);

  const size = 520;
  const center = { x: size / 2, y: size / 2 };
  const groupOrbitRadius = 158;
  const assetOrbitRadius = 29;

  const normalizedGroups = useMemo(
    () =>
      groups.map((group) => {
        const assets = Array.isArray(group.assets)
          ? group.assets
          : [];

        const totalArticles = assets.reduce(
          (total, asset) => total + getArticleCount(asset),
          0
        );

        return {
          ...group,
          assets,
          totalArticles,
        };
      }),
    [groups]
  );

  const maximumGroupArticles = useMemo(
    () =>
      Math.max(
        1,
        ...normalizedGroups.map((group) => group.totalArticles)
      ),
    [normalizedGroups]
  );

  const groupPositions = useMemo(() => {
    if (!normalizedGroups.length) return [];

    return normalizedGroups.map((group, index) => {
      const angle =
        -Math.PI / 2 +
        (index / normalizedGroups.length) * Math.PI * 2;

      return {
        ...group,
        angle,
        x: center.x + groupOrbitRadius * Math.cos(angle),
        y: center.y + groupOrbitRadius * Math.sin(angle),
      };
    });
  }, [normalizedGroups]);

  const activeGroup =
    activeGroupIndex !== null
      ? groupPositions[activeGroupIndex]
      : null;

  const handleGroupSelection = (group, index) => {
    setActiveGroupIndex(index);

    if (typeof onSelectGroup === "function") {
      onSelectGroup(group.group_name);
    }
  };

  if (!normalizedGroups.length) {
    return (
      <div className="monitoring-landscape monitoring-landscape-empty">
        <span>Monitoring landscape unavailable</span>
        <p>No monitoring groups are available for this view.</p>
      </div>
    );
  }

  return (
    <div className="monitoring-landscape">
      <div className="monitoring-landscape-canvas">
        <svg
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label="Relative news activity across monitoring groups"
          onMouseLeave={() => setActiveGroupIndex(null)}
        >
          <defs>
            <linearGradient
              id="landscapeSurface"
              x1="0"
              x2="0"
              y1="0"
              y2="1"
            >
              <stop offset="0%" stopColor="#0d1928" />
              <stop offset="100%" stopColor="#09131f" />
            </linearGradient>

            <radialGradient id="centerSurface">
              <stop offset="0%" stopColor="#1a3447" />
              <stop offset="100%" stopColor="#102332" />
            </radialGradient>
          </defs>

          <rect
            x="1"
            y="1"
            width={size - 2}
            height={size - 2}
            rx="18"
            fill="url(#landscapeSurface)"
            stroke="rgba(255,255,255,0.08)"
          />

{/* Static distance rings */}
{[58, 108, 158].map((radius) => (
  <circle
    key={radius}
    cx={center.x}
    cy={center.y}
    r={radius}
    fill="none"
    stroke="rgba(255,255,255,0.07)"
    strokeWidth="1"
  />
))}

{/* Subtle outward-moving radar pulses */}
{[0, 1, 2].map((pulseIndex) => (
  <circle
    key={`radar-pulse-${pulseIndex}`}
    className="news-radar-pulse"
    cx={center.x}
    cy={center.y}
    r="42"
    fill="none"
    stroke="rgba(63,160,192,0.34)"
    strokeWidth="1.25"
    style={{
      animationDelay: `${pulseIndex * 2.8}s`,
    }}
  />
))}

          <line
            x1={center.x}
            y1="62"
            x2={center.x}
            y2={size - 62}
            stroke="rgba(255,255,255,0.035)"
          />
          <line
            x1="62"
            y1={center.y}
            x2={size - 62}
            y2={center.y}
            stroke="rgba(255,255,255,0.035)"
          />

<circle
  cx={center.x}
  cy={center.y}
  r="40"
  fill="url(#centerSurface)"
  stroke="rgba(63,160,192,0.48)"
  strokeWidth="1.5"
/>

{/* <circle
  className="news-radar-center-pulse"
  cx={center.x}
  cy={center.y}
  r="5"
  fill="rgba(116,194,218,0.9)"
/> */}

          <text
            x={center.x}
            y={center.y - 6}
            textAnchor="middle"
            fill="#f4f7fb"
            fontSize="14"
            fontWeight="600"
          >
            Monitoring
          </text>
          <text
            x={center.x}
            y={center.y + 14}
            textAnchor="middle"
            fill="rgba(244,247,251,0.55)"
            fontSize="11"
          >
            {normalizedGroups.length} groups
          </text>

          {groupPositions.map((group, groupIndex) => {
            const isActive = groupIndex === activeGroupIndex;
            const relativeVolume =
              group.totalArticles / maximumGroupArticles;
            const groupRadius = 16 + relativeVolume * 8;
            const displayedAssets = group.assets.slice(0, 10);

            return (
              <g
                key={group.group_name}
                role="button"
                tabIndex="0"
                aria-label={`${group.group_name}, ${group.totalArticles} articles`}
                onMouseEnter={() => setActiveGroupIndex(groupIndex)}
                onFocus={() => setActiveGroupIndex(groupIndex)}
                onBlur={() => setActiveGroupIndex(null)}
                onClick={() =>
                  handleGroupSelection(group, groupIndex)
                }
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === " "
                  ) {
                    event.preventDefault();
                    handleGroupSelection(group, groupIndex);
                  }
                }}
                style={{ cursor: "pointer", outline: "none" }}
              >
                <line
                  x1={center.x}
                  y1={center.y}
                  x2={group.x}
                  y2={group.y}
                  stroke={
                    isActive
                      ? "rgba(63,160,192,0.48)"
                      : "rgba(255,255,255,0.09)"
                  }
                  strokeWidth={isActive ? "1.5" : "1"}
                />

                <circle
                  cx={group.x}
                  cy={group.y}
                  r={groupRadius + 8}
                  fill={
                    isActive
                      ? "rgba(63,160,192,0.10)"
                      : "rgba(255,255,255,0.018)"
                  }
                  stroke={
                    isActive
                      ? "rgba(63,160,192,0.30)"
                      : "rgba(255,255,255,0.055)"
                  }
                />

                <circle
                  cx={group.x}
                  cy={group.y}
                  r={groupRadius}
                  fill={
                    isActive
                      ? "rgba(36,91,119,0.96)"
                      : "rgba(22,48,67,0.96)"
                  }
                  stroke={
                    isActive
                      ? "rgba(98,184,211,0.85)"
                      : "rgba(255,255,255,0.16)"
                  }
                  strokeWidth="1.5"
                />

                <text
                  x={group.x}
                  y={group.y + 4}
                  textAnchor="middle"
                  fill="#f4f7fb"
                  fontSize="13"
                  fontWeight="700"
                  paintOrder="stroke"
                  stroke="rgba(7,16,27,0.75)"
                  strokeWidth="2"
                  pointerEvents="none"
                >
                  {group.totalArticles}
                </text>

                <text
                  x={group.x}
                  y={
                    group.y < center.y
                      ? group.y - groupRadius - 22
                      : group.y + groupRadius + 29
                  }
                  textAnchor="middle"
                  fill={
                    isActive
                      ? "#f4f7fb"
                      : "rgba(244,247,251,0.82)"
                  }
                  fontSize="13"
                  fontWeight="600"
                  paintOrder="stroke"
                  stroke="#09131f"
                  strokeWidth="4"
                  strokeLinejoin="round"
                  pointerEvents="none"
                >
                  {truncateLabel(group.group_name, 20)}
                </text>

                {displayedAssets.map((asset, assetIndex) => {
                  const assetAngle =
                    (assetIndex / displayedAssets.length) *
                    Math.PI *
                    2;
                  const assetX =
                    group.x +
                    assetOrbitRadius * Math.cos(assetAngle);
                  const assetY =
                    group.y +
                    assetOrbitRadius * Math.sin(assetAngle);
                  const articleCount = getArticleCount(asset);

                  return (
                    <circle
                      key={
                        asset.id ??
                        `${group.group_name}-${assetIndex}`
                      }
                      cx={assetX}
                      cy={assetY}
                      r={articleCount > 0 ? 4 : 3}
                      fill={
                        articleCount > 0
                          ? "rgba(111,181,205,0.82)"
                          : "rgba(244,247,251,0.20)"
                      }
                      stroke="rgba(7,16,27,0.95)"
                      strokeWidth="1.5"
                      pointerEvents="none"
                    />
                  );
                })}
              </g>
            );
          })}
        </svg>
      </div>

      <aside className="monitoring-landscape-details">
        {activeGroup ? (
          <>
            <div className="landscape-detail-header">
              <span>Selected monitoring group</span>
              <h3>{activeGroup.group_name}</h3>
            </div>

            <div className="landscape-detail-metrics">
              <div>
                <span>Total reporting</span>
                <strong>{activeGroup.totalArticles}</strong>
              </div>
              <div>
                <span>Monitored assets</span>
                <strong>{activeGroup.assets.length}</strong>
              </div>
            </div>

            <div className="landscape-asset-list">
              <span className="landscape-list-label">
                Highest reporting volume
              </span>

              {[...activeGroup.assets]
                .sort(
                  (a, b) =>
                    getArticleCount(b) - getArticleCount(a)
                )
                .slice(0, 6)
                .map((asset, index) => (
                  <div
                    className="landscape-asset-row"
                    key={
                      asset.id ??
                      `${activeGroup.group_name}-${index}`
                    }
                  >
                    <span>
                      {asset.firstname} {asset.lastname}
                    </span>
                    <strong>{getArticleCount(asset)}</strong>
                  </div>
                ))}
            </div>

            <button
              type="button"
              className="landscape-select-button"
              onClick={() =>
                handleGroupSelection(
                  activeGroup,
                  activeGroupIndex
                )
              }
            >
              Open group analysis
            </button>
          </>
        ) : (
          <div className="landscape-instructions">
            <span>Monitoring overview</span>
            <h3>Review reporting distribution</h3>
            <p>
              Hover over a monitoring group to review its reporting
              volume and most active assets. Select the group to open
              its analysis.
            </p>

            <div className="landscape-legend">
              <div>
                <span className="legend-marker legend-marker-group" />
                <p>Group size represents reporting volume</p>
              </div>
              <div>
                <span className="legend-marker legend-marker-asset" />
                <p>Outer markers represent monitored assets</p>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
};

export default NewsRadarDashboard;
